import React, { useEffect, useMemo, useState } from "react";
import { api, fmtMoney, formatErr } from "@/lib/api";
import { PageHeader, GlassCard, KpiCard, PrimaryButton, GhostButton } from "@/components/Primitives";
import RangeSelect, { DEFAULT_RANGE } from "@/components/RangeSelect";
import ExportButton from "@/components/ExportButton";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";
import {
  RefreshCw, DollarSign, Repeat, Users, CreditCard, AlertCircle,
  CheckCircle2, Clock, XCircle, ExternalLink, FileText, TrendingUp,
} from "lucide-react";
import { toast } from "sonner";

const tooltipStyle = {
  contentStyle: {
    background: "rgba(15,15,18,0.94)", border: "1px solid rgba(255,255,255,0.10)",
    borderRadius: 14, backdropFilter: "blur(20px)", color: "#fff", fontSize: 12,
  },
  labelStyle: { color: "rgba(255,255,255,0.5)" },
};

const STATUS_TONE = {
  paid: "bg-emerald-400/10 text-emerald-300 border-emerald-400/20",
  open: "bg-sky-400/10 text-sky-300 border-sky-400/20",
  draft: "bg-slate-400/10 text-slate-300 border-slate-400/20",
  void: "bg-rose-400/10 text-rose-300 border-rose-400/20",
  uncollectible: "bg-rose-400/10 text-rose-300 border-rose-400/20",
  active: "bg-emerald-400/10 text-emerald-300 border-emerald-400/20",
  trialing: "bg-sky-400/10 text-sky-300 border-sky-400/20",
  past_due: "bg-amber-400/10 text-amber-300 border-amber-400/20",
  canceled: "bg-rose-400/10 text-rose-300 border-rose-400/20",
  incomplete: "bg-slate-400/10 text-slate-300 border-slate-400/20",
};

function StatusBadge({ status }) {
  const tone = STATUS_TONE[status] || STATUS_TONE.draft;
  return (
    <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border ${tone}`}>
      {(status || "—").replace("_", " ")}
    </span>
  );
}

function fmtStripeTs(ts) {
  if (!ts) return "—";
  try { return new Date(ts * 1000).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }); }
  catch { return "—"; }
}

function fmtIsoDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); }
  catch { return "—"; }
}

export default function Billing() {
  const [dash, setDash] = useState(null);
  const [subs, setSubs] = useState([]);
  const [invs, setInvs] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [syncing, setSyncing] = useState(false);
  const [tab, setTab] = useState("subscriptions");
  const [range, setRange] = useState(DEFAULT_RANGE);

  const load = async () => {
    try {
      const [d, s, i, c] = await Promise.all([
        api.get("/billing-dashboard"),
        api.get("/stripe/subscriptions"),
        api.get("/stripe/invoices"),
        api.get("/stripe/customers"),
      ]);
      setDash(d.data); setSubs(s.data); setInvs(i.data); setCustomers(c.data);
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  const sync = async (silent = false) => {
    setSyncing(true);
    try {
      const { data } = await api.post("/stripe/sync");
      if (!silent) toast.success(`Synced ${data.customers} customers · ${data.subscriptions} subs · ${data.invoices} invoices`);
      await load();
    } catch (e) {
      if (!silent) toast.error(formatErr(e));
    } finally {
      setSyncing(false);
    }
  };

  // Auto-sync on load (Q2 = b: on-demand + auto-refresh on dashboard load)
  useEffect(() => {
    (async () => {
      await load();
      await sync(true);
    })();
  }, []);

  const notConfigured = dash && !dash.configured;
  const hasNoData = dash && dash.customer_count === 0 && dash.active_subscriptions === 0 && dash.paid_invoices === 0;

  const subRows = useMemo(() => {
    const custMap = Object.fromEntries(customers.map((c) => [c.stripe_id, c]));
    return subs.map((s) => ({ ...s, _customer: custMap[s.customer] }));
  }, [subs, customers]);

  const invRows = useMemo(() => {
    const custMap = Object.fromEntries(customers.map((c) => [c.stripe_id, c]));
    return invs.map((i) => ({ ...i, _customer: custMap[i.customer] }));
  }, [invs, customers]);

  if (!dash) return <div className="text-white/40" data-testid="billing-loading">Loading…</div>;

  return (
    <div data-testid="billing-page">
      <PageHeader
        title="Billing"
        subtitle="Read-only Stripe insights. Equinox observes — Stripe handles all billing."
        testId="billing-header"
        action={
          <div className="flex items-center gap-2.5 flex-wrap">
            <div className="text-[11px] text-white/35 hidden md:block">
              Last synced: {fmtIsoDate(dash.last_synced_at)}
            </div>
            <RangeSelect value={range} onChange={setRange} testIdPrefix="billing-range" />
            <ExportButton endpoint="/exports/invoices" range={range} testId="billing-export-btn" label="Export" />
            <PrimaryButton onClick={() => sync(false)} disabled={syncing} data-testid="billing-sync-btn">
              <RefreshCw className={`w-4 h-4 ${syncing ? "animate-spin" : ""}`} />
              {syncing ? "Syncing…" : "Sync Stripe"}
            </PrimaryButton>
          </div>
        }
      />

      {notConfigured && (
        <GlassCard className="mb-6 border-rose-400/20 !bg-rose-500/[0.04]" testId="stripe-not-configured">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-rose-400 mt-0.5" />
            <div>
              <div className="font-medium text-white">Stripe not configured</div>
              <div className="text-sm text-white/55 mt-1">
                Set <code className="text-white/80">STRIPE_API_KEY</code> in <code className="text-white/80">backend/.env</code> with a read-only restricted key.
              </div>
            </div>
          </div>
        </GlassCard>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 mb-8">
        <KpiCard testId="kpi-revenue" label="Total Revenue" value={fmtMoney(dash.total_revenue)} icon={DollarSign} accent="from-emerald-400 to-emerald-600" hint={`${dash.paid_invoices} paid invoices`} />
        <KpiCard testId="kpi-mrr" label="MRR" value={fmtMoney(dash.mrr)} icon={Repeat} accent="from-[#007AFF] to-[#0050C8]" hint={`ARR ${fmtMoney(dash.arr)}`} />
        <KpiCard testId="kpi-outstanding" label="Outstanding" value={fmtMoney(dash.outstanding)} icon={Clock} accent="from-amber-400 to-amber-600" hint={`${dash.open_invoices} open invoices`} />
        <KpiCard testId="kpi-customers" label="Customers" value={dash.customer_count} icon={Users} accent="from-violet-400 to-violet-600" hint={`${dash.active_subscriptions} active subs`} />
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 mb-8">
        <KpiCard testId="kpi-active" label="Active Subs" value={dash.active_subscriptions} icon={CheckCircle2} accent="from-emerald-400 to-emerald-600" />
        <KpiCard testId="kpi-trialing" label="Trialing" value={dash.trialing_subscriptions} icon={Clock} accent="from-sky-400 to-sky-600" />
        <KpiCard testId="kpi-past-due" label="Past Due" value={dash.past_due_subscriptions} icon={AlertCircle} accent="from-amber-400 to-amber-600" />
        <KpiCard testId="kpi-churned" label="Churned (90d)" value={`${dash.churn_rate_90d}%`} icon={XCircle} accent="from-rose-400 to-rose-600" hint={`${dash.churned_subscriptions} total churned`} />
      </div>

      <GlassCard className="mb-8" testId="billing-revenue-chart">
        <div className="mb-5 flex items-end justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-white/40">Stripe Revenue · last 12 months</div>
            <div className="font-display text-2xl font-light mt-1">Paid invoices</div>
          </div>
          <TrendingUp className="w-5 h-5 text-[#007AFF]/70" />
        </div>
        <ResponsiveContainer width="100%" height={240}>
          <AreaChart data={dash.revenue_series}>
            <defs>
              <linearGradient id="rev" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#007AFF" stopOpacity={0.45} />
                <stop offset="95%" stopColor="#007AFF" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid stroke="rgba(255,255,255,0.04)" vertical={false} />
            <XAxis dataKey="label" stroke="rgba(255,255,255,0.35)" fontSize={11} tickLine={false} axisLine={false} />
            <YAxis stroke="rgba(255,255,255,0.35)" fontSize={11} tickLine={false} axisLine={false}
                   tickFormatter={(v) => v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v} />
            <Tooltip {...tooltipStyle} formatter={(v) => fmtMoney(v)} cursor={{ stroke: "rgba(0,122,255,0.4)", strokeWidth: 1, fill: "transparent" }} />
            <Area type="monotone" dataKey="revenue" stroke="#007AFF" strokeWidth={2} fill="url(#rev)" />
          </AreaChart>
        </ResponsiveContainer>
      </GlassCard>

      {hasNoData && (
        <GlassCard className="mb-8 text-center" testId="billing-empty">
          <CreditCard className="w-10 h-10 mx-auto text-white/25 mb-3" />
          <div className="text-white/75 font-medium">No Stripe data yet</div>
          <div className="text-sm text-white/45 mt-1">
            Once you have customers, subscriptions, or invoices in Stripe, hit <span className="text-white">Sync Stripe</span> to pull them in.
          </div>
        </GlassCard>
      )}

      <div className="flex items-center gap-2 mb-4">
        {[
          ["subscriptions", `Subscriptions (${subs.length})`],
          ["invoices", `Invoices (${invs.length})`],
          ["customers", `Customers (${customers.length})`],
        ].map(([k, label]) => (
          <button
            key={k}
            onClick={() => setTab(k)}
            data-testid={`billing-tab-${k}`}
            className={`px-4 py-1.5 rounded-full text-sm transition ${
              tab === k ? "bg-[#007AFF] text-white" : "bg-white/[0.04] hover:bg-white/[0.08] text-white/65"
            }`}
          >{label}</button>
        ))}
      </div>

      {tab === "subscriptions" && (
        <GlassCard testId="billing-subs-table">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-[0.18em] text-white/40">
                  <th className="py-3 pr-4">Customer</th>
                  <th className="py-3 pr-4">Status</th>
                  <th className="py-3 pr-4 text-right">Monthly</th>
                  <th className="py-3 pr-4">Plan</th>
                  <th className="py-3 pr-4">Period ends</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/[0.05]">
                {subRows.map((s) => (
                  <tr key={s.stripe_id} className="hover:bg-white/[0.03]" data-testid={`sub-row-${s.stripe_id}`}>
                    <td className="py-3 pr-4 text-white">{s._customer?.name || s._customer?.email || s.customer}</td>
                    <td className="py-3 pr-4"><StatusBadge status={s.status} /></td>
                    <td className="py-3 pr-4 text-right font-medium text-emerald-300">{fmtMoney(s.monthly_amount)}</td>
                    <td className="py-3 pr-4 text-white/60">
                      {(s.items || []).map((it) => it.nickname || `${it.interval || "?"}`).join(", ") || "—"}
                    </td>
                    <td className="py-3 pr-4 text-white/55">{fmtStripeTs(s.current_period_end)}</td>
                  </tr>
                ))}
                {subRows.length === 0 && (
                  <tr><td colSpan={5} className="py-10 text-center text-white/40">No subscriptions found in Stripe.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </GlassCard>
      )}

      {tab === "invoices" && (
        <GlassCard testId="billing-invs-table">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-[0.18em] text-white/40">
                  <th className="py-3 pr-4">Number</th>
                  <th className="py-3 pr-4">Customer</th>
                  <th className="py-3 pr-4">Status</th>
                  <th className="py-3 pr-4 text-right">Total</th>
                  <th className="py-3 pr-4 text-right">Paid</th>
                  <th className="py-3 pr-4">Created</th>
                  <th className="py-3 pr-4"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/[0.05]">
                {invRows.map((i) => (
                  <tr key={i.stripe_id} className="hover:bg-white/[0.03]" data-testid={`inv-row-${i.stripe_id}`}>
                    <td className="py-3 pr-4 text-white font-medium">{i.number || i.stripe_id.slice(-8)}</td>
                    <td className="py-3 pr-4 text-white/70">{i._customer?.name || i.customer_email || i.customer}</td>
                    <td className="py-3 pr-4"><StatusBadge status={i.status} /></td>
                    <td className="py-3 pr-4 text-right">{fmtMoney(i.total)}</td>
                    <td className="py-3 pr-4 text-right text-emerald-300">{fmtMoney(i.amount_paid)}</td>
                    <td className="py-3 pr-4 text-white/55">{fmtStripeTs(i.created)}</td>
                    <td className="py-3 pr-4 text-right">
                      {i.hosted_invoice_url && (
                        <a href={i.hosted_invoice_url} target="_blank" rel="noreferrer"
                           className="text-[#007AFF] hover:text-[#3596ff] p-1.5 rounded-lg hover:bg-[#007AFF]/10 inline-flex"
                           data-testid={`inv-link-${i.stripe_id}`} title="View on Stripe">
                          <ExternalLink className="w-3.5 h-3.5" />
                        </a>
                      )}
                    </td>
                  </tr>
                ))}
                {invRows.length === 0 && (
                  <tr><td colSpan={7} className="py-10 text-center text-white/40">
                    <FileText className="w-8 h-8 mx-auto mb-2 opacity-40" />
                    No invoices found in Stripe.
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        </GlassCard>
      )}

      {tab === "customers" && (
        <GlassCard testId="billing-customers-table">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-[0.18em] text-white/40">
                  <th className="py-3 pr-4">Name</th>
                  <th className="py-3 pr-4">Email</th>
                  <th className="py-3 pr-4">Stripe ID</th>
                  <th className="py-3 pr-4">Created</th>
                  <th className="py-3 pr-4">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/[0.05]">
                {customers.map((c) => (
                  <tr key={c.stripe_id} className="hover:bg-white/[0.03]" data-testid={`cust-row-${c.stripe_id}`}>
                    <td className="py-3 pr-4 text-white">{c.name || "—"}</td>
                    <td className="py-3 pr-4 text-white/70">{c.email || "—"}</td>
                    <td className="py-3 pr-4 text-white/40 font-mono text-xs">{c.stripe_id}</td>
                    <td className="py-3 pr-4 text-white/55">{fmtStripeTs(c.created)}</td>
                    <td className="py-3 pr-4">
                      {c.delinquent
                        ? <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border bg-rose-400/10 text-rose-300 border-rose-400/20">Delinquent</span>
                        : <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border bg-emerald-400/10 text-emerald-300 border-emerald-400/20">OK</span>}
                    </td>
                  </tr>
                ))}
                {customers.length === 0 && (
                  <tr><td colSpan={5} className="py-10 text-center text-white/40">No customers found in Stripe.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </GlassCard>
      )}
    </div>
  );
}
